import { useState, useContext, useEffect } from 'react'
import styles from './ProjectsTable.module.css'
import { SplitViewContext, DataRefreshContext } from './ManageSection'
import apiClient from '../utils/api'
import CreateProjectModal from './modals/CreateProjectModal'
import CreateTaskModal from './modals/CreateTaskModal'
import ProjectDetails from './details/ProjectDetails'
import { formatDisplayDate } from '../utils/dateUtils'

export default function ProjectsTable({ apiToken, selectedProjectId }) {
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState('active')
    const [clientFilter, setClientFilter] = useState('all')
    const [accessFilter, setAccessFilter] = useState('all')
    const [showCreateModal, setShowCreateModal] = useState(false)
    const [showCreateTaskModal, setShowCreateTaskModal] = useState(false)
    const [editingTask, setEditingTask] = useState(null)
    const [selectedProject, setSelectedProject] = useState(null)
    const [activeTab, setActiveTab] = useState('overview')
    const [projects, setProjects] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [clients, setClients] = useState([])
    const [users, setUsers] = useState([])
    const [groups, setGroups] = useState([])
    const [successMessage, setSuccessMessage] = useState('')
    const [errorMessage, setErrorMessage] = useState('')

    // Get split view context
    const splitViewContext = useContext(SplitViewContext)
    const { setIsInSplitView } = splitViewContext || {}

    // Get data refresh context
    const dataRefreshContext = useContext(DataRefreshContext)
    const { refreshKey } = dataRefreshContext || { refreshKey: 0 }

    // Update split view state when project selection changes
    useEffect(() => {
        if (setIsInSplitView) {
            setIsInSplitView(!!selectedProject)
        }
    }, [selectedProject, setIsInSplitView])

    // Load projects data from backend
    useEffect(() => {
        const loadData = async () => {
            if (!apiToken) return

            try {
                setLoading(true)
                setError(null)

                apiClient.setAuthToken(apiToken)

                // Load projects, clients, users, and groups data
                const [projectsResponse, clientsResponse, usersResponse, groupsResponse] = await Promise.all([
                    apiClient.getProjects(),
                    apiClient.getClients(),
                    apiClient.getTeammates(),
                    apiClient.getGroups()
                ])

                setProjects(projectsResponse.projects || [])
                setClients(clientsResponse.clients || [])
                setUsers(usersResponse.teammates || [])
                setGroups(groupsResponse.groups || [])
            } catch (err) {
                console.error('Failed to load projects:', err)
                setError('Failed to load projects data')
            } finally {
                setLoading(false)
            }
        }

        loadData()
    }, [apiToken, refreshKey])

    // Effect to set selected project from URL or prop when projects are loaded
    useEffect(() => {
        if (projects.length > 0) {
            const projectIdFromUrl = getProjectIdFromUrl()
            const targetProjectId = projectIdFromUrl || selectedProjectId

            if (targetProjectId) {
                const project = projects.find(p => p.id === targetProjectId)
                if (project) {
                    setSelectedProject(project)
                }
            }
        }
    }, [projects, selectedProjectId])

    // Effect to handle browser back/forward navigation
    useEffect(() => {
        const handlePopState = () => {
            const projectIdFromUrl = getProjectIdFromUrl()
            if (projectIdFromUrl) {
                const project = projects.find(p => p.id === projectIdFromUrl)
                if (project) {
                    setSelectedProject(project)
                } else {
                    setSelectedProject(null)
                }
            } else {
                setSelectedProject(null)
            }
        }

        window.addEventListener('popstate', handlePopState)
        return () => {
            window.removeEventListener('popstate', handlePopState)
        }
    }, [projects])

    // Function to get project ID from URL
    const getProjectIdFromUrl = () => {
        const path = window.location.pathname
        const match = path.match(/^\/manage\/projects\/(.+)$/)
        return match ? match[1] : null
    }

    // Filter projects based on search and status
    const filteredProjects = projects.filter(project => {
        const matchesSearch = (project.name && project.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (project.client_name && project.client_name.toLowerCase().includes(searchTerm.toLowerCase()))
        const matchesStatus = statusFilter === 'all' || project.status === statusFilter
        const matchesClient = clientFilter === 'all' || (project.client_name && project.client_name === clientFilter)

        // Access filter - check if the selected accessor has access to this project
        const matchesAccess = accessFilter === 'all' ||
            (project.accessor_ids && project.accessor_ids.includes(accessFilter))

        return matchesSearch && matchesStatus && matchesClient && matchesAccess
    })

    const getStatusBadge = (status) => {
        const statusClass = {
            'active': styles.statusActive,
            'inactive': styles.statusInactive,
            'archived': styles.statusArchived
        }[status] || styles.statusActive

        const statusText = {
            'active': 'Active',
            'inactive': 'Inactive',
            'archived': 'Archived'
        }[status] || status

        return (
            <span className={`${styles.statusBadge} ${statusClass}`}>
                {statusText}
            </span>
        )
    }

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount)
    }

    const formatDate = (dateString) => {
        return formatDisplayDate(dateString)
    }

    const handleProjectSelect = (project) => {
        setSelectedProject(project)
        setActiveTab('overview') // Reset to overview tab when selecting a new project
        // Update URL to include project ID
        const newUrl = `/manage/projects/${project.id}`
        window.history.pushState({ projectId: project.id }, '', newUrl)
    }

    const handleCloseDetails = () => {
        setSelectedProject(null)
        // Update URL to remove project ID
        window.history.pushState({}, '', '/manage/projects')
    }

    const handleSuccessMessage = (message) => {
        setSuccessMessage(message)
        setTimeout(() => setSuccessMessage(''), 8000) // Increased from 5000ms to 8000ms for better readability
    }

    const handleError = (message) => {
        setErrorMessage(message)
        setTimeout(() => setErrorMessage(''), 5000)
    }

    const handleProjectUpdate = (updatedProject) => {
        setSelectedProject(updatedProject)
        // Update the project in the projects list
        setProjects(prev => prev.map(p => p.id === updatedProject.id ? updatedProject : p))
    }

    const handleProjectCreated = (response) => {
        // Add the new project to the list
        const newProject = response.project || response
        setProjects(prevProjects => [...prevProjects, newProject])
        setShowCreateModal(false)
    }

    const refreshProjectsData = async () => {
        // Refresh projects data to get updated task list
        if (!apiToken) return

        try {
            apiClient.setAuthToken(apiToken)
            const response = await apiClient.getProjects()
            const updatedProjects = response.projects || []
            setProjects(updatedProjects)

            // Update selected project if it exists
            if (selectedProject) {
                const updatedProject = updatedProjects.find(p => p.id === selectedProject.id)
                if (updatedProject) {
                    setSelectedProject(updatedProject)
                }
            }
        } catch (err) {
            console.error('Failed to refresh projects:', err)
        }
    }

    const handleTaskCreated = (newTask) => {
        // Refresh project data to show the new task
        refreshProjectsData()
    }

    const handleTaskUpdated = (updatedTask) => {
        // Refresh project data to show the updated task
        refreshProjectsData()
        setEditingTask(null)
    }

    const handleEditTask = (task) => {
        setEditingTask(task)
        setShowCreateTaskModal(true)
    }

    const handleCloseTaskModal = () => {
        setShowCreateTaskModal(false)
        setEditingTask(null)
    }

    return (
        <div className={`${styles.projectsContainer} ${selectedProject ? styles.split : ''}`}>
            {/* Success/Error Messages */}
            {successMessage && (
                <div className={styles.successMessage}>
                    {successMessage}
                </div>
            )}
            {errorMessage && (
                <div className={styles.errorMessage}>
                    {errorMessage}
                </div>
            )}
            <div className={`${styles.splitView} ${selectedProject ? styles.split : ''}`}>
                {/* Main Projects Content */}
                <div className={selectedProject ? styles.projectsSidebar : styles.projectsMain}>
                    {/* Header */}
                    {!selectedProject && (
                        <div className={styles.header}>
                            <div className={styles.headerLeft}>
                                <h2>Projects</h2>
                                <p>Manage and track your projects</p>
                            </div>
                            <button
                                className={styles.createButton}
                                onClick={() => setShowCreateModal(true)}
                            >
                                + Create Project
                            </button>
                        </div>
                    )}

                    {/* Conditional Content: Show full table or simple list based on selection */}
                    {!selectedProject ? (
                        <>
                            {/* Search and Filters */}
                            <div className={styles.controls}>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className={styles.filterSelect}
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                    <option value="archived">Archived</option>
                                    <option value="all">All Status</option>
                                </select>
                                <select
                                    value={clientFilter}
                                    onChange={(e) => setClientFilter(e.target.value)}
                                    className={styles.filterSelect}
                                >
                                    <option value="all">All Clients</option>
                                    {clients.map(client => (
                                        <option key={client.id} value={client.name}>
                                            {client.name}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    value={accessFilter}
                                    onChange={(e) => setAccessFilter(e.target.value)}
                                    className={styles.filterSelect}
                                >
                                    <option value="all">All Access</option>
                                    <optgroup label="Groups">
                                        {groups.map(group => (
                                            <option key={`group-${group.id}`} value={`group-${group.id}`}>
                                                {group.name}
                                            </option>
                                        ))}
                                    </optgroup>
                                    <optgroup label="Users">
                                        {users.map(user => (
                                            <option key={`user-${user.id}`} value={`user-${user.id}`}>
                                                {user.full_name || user.display_name || user.email}
                                            </option>
                                        ))}
                                    </optgroup>
                                </select>
                                <div className={styles.searchBox}>
                                    <input
                                        type="text"
                                        placeholder="Search projects..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className={`${styles.searchInput}`}
                                    />
                                    <span className={styles.searchIcon}>🔍</span>
                                </div>
                            </div>

                            {/* Projects Table */}
                            <div className={styles.tableContainer}>
                                <table className={styles.projectsTable}>
                                    <thead>
                                        <tr>
                                            <th>Project Name</th>
                                            <th>Client</th>
                                            <th>Status</th>
                                            <th>Color</th>
                                            <th>Tasks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="6" className={`${styles.noResults}`}>
                                                    Loading projects...
                                                </td>
                                            </tr>
                                        ) : error ? (
                                            <tr>
                                                <td colSpan="6" className={`${styles.noResults}`}>
                                                    {error}
                                                </td>
                                            </tr>
                                        ) : filteredProjects.length > 0 ? (
                                            filteredProjects.map(project => (
                                                <tr
                                                    key={project.id}
                                                    onClick={() => handleProjectSelect(project)}
                                                    className={styles.tableRow}
                                                >
                                                    <td>
                                                        <div className={styles.projectName}>
                                                            <span className={`${styles.name}`}>{project.name}</span>
                                                            <span className={`${styles.description}`}>{project.description || 'No description'}</span>
                                                        </div>
                                                    </td>
                                                    <td>{project.client_name || 'No Client'}</td>
                                                    <td>{getStatusBadge(project.status)}</td>
                                                    <td>
                                                        <div
                                                            className={styles.colorIndicator}
                                                            style={{ backgroundColor: project.color || '#ccc' }}
                                                            title={project.color || 'No color set'}
                                                        >
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {project.tasks_count || 0} tasks
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className={`${styles.noResults}`}>
                                                    No projects found matching your criteria
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    ) : (
                        /* Simple Projects List for Split View */
                        <div className={styles.projectsListContainer}>
                            <div className={styles.listHeader}>
                                <h3>Projects</h3>
                                <div className={styles.listHeaderRight}>
                                    <span className={styles.projectCount}>{filteredProjects.length} projects</span>
                                    <button
                                        className={styles.createButtonCompact}
                                        onClick={() => setShowCreateModal(true)}
                                        aria-label="Create Project"
                                        title="Create Project"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>
                            <div className={styles.projectsList}>
                                {filteredProjects.map(project => (
                                    <div
                                        key={project.id}
                                        className={`${styles.projectListItem} ${project.id === selectedProject?.id ? styles.active : ''}`}
                                        onClick={() => handleProjectSelect(project)}
                                    >
                                        <div className={`${styles.projectListName}`}>{project.name}</div>
                                        <div className={`${styles.projectListClient}`}>{project.client_name || 'No Client'}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Project Details Panel */}
                {selectedProject && (
                    <ProjectDetails
                        key={selectedProject.id}
                        project={selectedProject}
                        onClose={handleCloseDetails}
                        apiToken={apiToken}
                        onSuccessMessage={handleSuccessMessage}
                        onError={handleError}
                        onProjectUpdate={handleProjectUpdate}
                    />
                )}
            </div>

            {/* Create Project Modal */}
            <CreateProjectModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSave={handleProjectCreated}
                clients={clients}
                apiToken={apiToken}
            />

            {/* Create Task Modal */}
            <CreateTaskModal
                isOpen={showCreateTaskModal}
                onClose={handleCloseTaskModal}
                onSave={editingTask ? handleTaskUpdated : handleTaskCreated}
                project={selectedProject}
                task={editingTask}
                apiToken={apiToken}
            />
        </div>
    )
}